import { useState } from 'react';

export interface QuickFilter<T> {
  readonly query: string;
  setQuery(next: string): void;
  /** The items that match, in their original order. */
  readonly results: readonly T[];
  readonly isFiltering: boolean;
  readonly totalCount: number;
  readonly matchCount: number;
}

/**
 * Client-side filtering for a list the user is already looking at.
 *
 * Every word typed must be the **start of a word** somewhere in the item, and
 * matching ignores case and accents. So "chal nor" finds the north room of the
 * Chalet, "bebe" finds "Chambre Bébé", and "jean" finds "Saint-Jean" — while
 * "Virgo 02" does not drag in "Virgo 01" the way a plain substring search does,
 * because "02" appears inside "2026".
 *
 * Filtering runs on every keystroke over lists of this size, so there is
 * nothing to memoise: the work is smaller than the render it feeds.
 */
export function useQuickFilter<T>(
  items: readonly T[],
  toSearchableFields: (item: T) => readonly (string | number | null | undefined)[],
): QuickFilter<T> {
  const [query, setQuery] = useState('');

  const tokens = toWords(query);
  const results =
    tokens.length === 0
      ? items
      : items.filter((item) => {
          const words = toWords(
            toSearchableFields(item)
              .filter((field) => field !== null && field !== undefined)
              .join(' '),
          );
          return tokens.every((token) => words.some((word) => word.startsWith(token)));
        });

  return {
    query,
    setQuery,
    results,
    isFiltering: tokens.length > 0,
    totalCount: items.length,
    matchCount: results.length,
  };
}

/**
 * Lower-cases, strips accents and cuts on anything that is not a letter or a
 * digit, so punctuation and hyphens never hide a word from the search.
 */
function toWords(value: string): string[] {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}
